import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { api } from '../context/AuthContext';
import ProducerCard from '../components/ProducerCard';
import { useSEO } from '../hooks/useSEO';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const ProducersPage = () => {
  useSEO({
    title: 'Produttori',
    description: 'Scopri i produttori artigianali del Sud Italia. Storie autentiche, prodotti di qualità, tradizioni locali. Acquista direttamente dalla fonte.',
    canonicalPath: '/produttori',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [producers, setProducers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  
  const [filters, setFilters] = useState({
    region: searchParams.get('region') || '',
    search: searchParams.get('search') || '',
    page: parseInt(searchParams.get('page') || '1')
  });

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const res = await api.get('/catalog/regions');
        setRegions(res.data);
      } catch (error) {
        console.error('Error fetching regions:', error);
      }
    };
    fetchRegions();
  }, []);

  useEffect(() => {
    const fetchProducers = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.region) params.set('region', filters.region);
        if (filters.search) params.set('search', filters.search);
        params.set('page', filters.page.toString());
        params.set('page_size', '12');

        const res = await api.get(`/producers?${params.toString()}`);
        setProducers(res.data.items);
        setPagination({
          total: res.data.total,
          page: res.data.page,
          pages: res.data.pages
        });
      } catch (error) {
        console.error('Error fetching producers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducers();

    const params = new URLSearchParams();
    if (filters.region) params.set('region', filters.region);
    if (filters.search) params.set('search', filters.search);
    if (filters.page > 1) params.set('page', filters.page.toString());
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  return (
    <div className="min-h-screen bg-background" data-testid="producers-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-text-primary">
            I Nostri Produttori
          </h1>
          <p className="mt-2 text-text-secondary">
            Artigiani e agricoltori del Sud Italia che portano avanti tradizioni centenarie
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              type="text"
              placeholder="Cerca produttori..."
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              className="pl-10"
              data-testid="search-producers-input"
            />
          </div>
          
          <Select value={filters.region || "all"} onValueChange={(v) => updateFilter('region', v === "all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="region-filter">
              <SelectValue placeholder="Regione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le regioni</SelectItem>
              {regions.map(reg => (
                <SelectItem key={reg.id} value={reg.id}>{reg.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results */}
        <div className="mb-4 text-sm text-text-secondary">
          {pagination.total} produttori trovati
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                <div className="w-24 h-24 mx-auto bg-gray-200 rounded-full" />
                <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mt-4" />
              </div>
            ))}
          </div>
        ) : producers.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {producers.map(producer => (
                <ProducerCard key={producer.id} producer={producer} />
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="mt-8 flex justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={pagination.page === 1}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Precedente
                </Button>
                <span className="flex items-center px-4 text-sm text-text-secondary">
                  Pagina {pagination.page} di {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  disabled={pagination.page === pagination.pages}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Successiva
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16 bg-white rounded-xl">
            <p className="text-text-secondary">Nessun produttore trovato</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProducersPage;
